/**
 * ч.1354: сесія сторінки «Імпорт виписки» живе поза сторінкою (рішення власника:
 * «нажал импорт выписки — подтянуло — запустил агента — ушёл на другую страницу — вернулся,
 * а там пусто»).
 *
 * Розібрана виписка, вибір рахунку/джерела, правки рядків і підказки Oblik+/AI тримались у
 * useState сторінки: перехід на іншу сторінку розмонтовує її — все зникало, а відповідь
 * агента, що прийшла після переходу, нікуди не лягала. Тепер ці поля — у сховищі zustand з
 * дзеркалом у localStorage (як чернетки документів, `store/formDrafts`): повернення на
 * сторінку показує те саме, відповідь агента лягає у сховище і без сторінки.
 *
 * Межі: лише браузер користувача (у базу нічого не пишемо — виписка до «створити» ніде не
 * збережена, це свідомо); акаунт (tenant) у ключі — чужа сесія не показується (Норма #37);
 * живе 10 годин, як чернетки; запис у localStorage — з паузою 400 мс (виписка на тисячу
 * рядків ≈ 1 МБ, писати на кожне натискання не можна). Файл (`File`) не зберігається —
 * після розбору він уже не потрібен. Режим «нерозпізнані» (review) — окреме, незбережене
 * сховище: там рядки — існуючі документи, їх тягне сервер.
 */
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { useAuth } from '@/auth/store';
import { DRAFT_TTL_MS } from './formDrafts';

export const BANK_IMPORT_SESSION_KEY = 'bankImportSession.v1';
const WRITE_DELAY_MS = 400;

export interface BankImportSessionData<P = unknown, R = unknown, A = unknown> {
  tenantId: string | null;
  /** час останньої зміни — для строку життя (10 годин, як чернетки) */
  ts: number;
  orgId: string;
  bankId: string;
  source: 'file' | 'api' | '';
  apiFrom: string;
  apiTo: string;
  formatChoice: string;
  preview: P | null;
  rows: R[];
  applyResult: A | null;
  assistNote: string | null;
  /** агент ще працює — не зберігається (після перезавантаження запиту вже немає) */
  assistBusy: boolean;
}

type Updater<T> = T | ((prev: T) => T);

export interface BankImportSessionState<P = unknown, R = unknown, A = unknown>
  extends BankImportSessionData<P, R, A> {
  setOrgId: (v: string) => void;
  setBankId: (v: string) => void;
  setSource: (v: 'file' | 'api' | '') => void;
  setApiFrom: (v: string) => void;
  setApiTo: (v: string) => void;
  setFormatChoice: (v: string) => void;
  setPreview: (v: P | null) => void;
  setRows: (v: Updater<R[]>) => void;
  setApplyResult: (v: A | null) => void;
  setAssistNote: (v: string | null) => void;
  setAssistBusy: (v: boolean) => void;
  /** скинути сесію (інший акаунт, строк вийшов, «почати заново») */
  reset: () => void;
}

function tenantNow(): string | null {
  return useAuth.getState().activeTenantId ?? null;
}

export function emptySession<P, R, A>(): BankImportSessionData<P, R, A> {
  return {
    tenantId: tenantNow(), ts: Date.now(),
    orgId: '', bankId: '', source: '', apiFrom: '', apiTo: '', formatChoice: '',
    preview: null, rows: [], applyResult: null, assistNote: null, assistBusy: false,
  };
}

/** Чи придатна збережена сесія: той самий акаунт і не старша за строк життя. */
export function sessionIsUsable(s: Pick<BankImportSessionData, 'tenantId' | 'ts'>, now = Date.now()): boolean {
  return s.tenantId === tenantNow() && now - s.ts <= DRAFT_TTL_MS;
}

/** localStorage з паузою запису: багато дрібних правок рядків — один запис. */
export function debouncedLocalStorage(delayMs = WRITE_DELAY_MS): StateStorage {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { name: string; value: string } | null = null;
  const flush = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!pending) return;
    try { localStorage.setItem(pending.name, pending.value); } catch { /* приватний режим / переповнене сховище */ }
    pending = null;
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
  }
  return {
    getItem: (name) => { try { return localStorage.getItem(name); } catch { return null; } },
    setItem: (name, value) => {
      pending = { name, value };
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, delayMs);
    },
    removeItem: (name) => {
      pending = null;
      if (timer) { clearTimeout(timer); timer = null; }
      try { localStorage.removeItem(name); } catch { /* ignore */ }
    },
  };
}

type SessionStore<P, R, A> = UseBoundStore<StoreApi<BankImportSessionState<P, R, A>>>;

function actions<P, R, A>(
  set: StoreApi<BankImportSessionState<P, R, A>>['setState'],
): Omit<BankImportSessionState<P, R, A>, keyof BankImportSessionData<P, R, A>> {
  const touch = <K extends keyof BankImportSessionData<P, R, A>>(k: K) =>
    (v: BankImportSessionData<P, R, A>[K]) => set({ [k]: v, ts: Date.now() } as Partial<BankImportSessionState<P, R, A>>);
  return {
    setOrgId: touch('orgId'), setBankId: touch('bankId'), setSource: touch('source'),
    setApiFrom: touch('apiFrom'), setApiTo: touch('apiTo'), setFormatChoice: touch('formatChoice'),
    setPreview: touch('preview'), setApplyResult: touch('applyResult'),
    setAssistNote: touch('assistNote'),
    setAssistBusy: (v) => set({ assistBusy: v }),
    setRows: (v) => set((s) => ({
      rows: typeof v === 'function' ? (v as (prev: R[]) => R[])(s.rows) : v, ts: Date.now(),
    })),
    reset: () => set({ ...emptySession<P, R, A>() }),
  };
}

/** Сховище сесії: з дзеркалом у localStorage (сторінка) або лише в памʼяті (режим review). */
export function createBankImportSession<P = unknown, R = unknown, A = unknown>(
  opts: { persistKey?: string; storage?: StateStorage } = {},
): SessionStore<P, R, A> {
  if (!opts.persistKey) {
    return create<BankImportSessionState<P, R, A>>()((set) => ({ ...emptySession<P, R, A>(), ...actions<P, R, A>(set) }));
  }
  return create<BankImportSessionState<P, R, A>>()(persist(
    (set) => ({ ...emptySession<P, R, A>(), ...actions<P, R, A>(set) }),
    {
      name: opts.persistKey,
      storage: createJSONStorage(() => opts.storage ?? debouncedLocalStorage()),
      partialize: (s) => {
        const { assistBusy, ...data } = s;
        void assistBusy;
        // лише дані, без функцій
        return Object.fromEntries(Object.entries(data).filter(([, v]) => typeof v !== 'function')) as Partial<BankImportSessionState<P, R, A>>;
      },
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<BankImportSessionData<P, R, A>>;
        // чужий акаунт або строк вийшов — чиста сесія (Норма #37: одне життя сторінки = один акаунт)
        if (typeof p.ts !== 'number' || !sessionIsUsable({ tenantId: p.tenantId ?? null, ts: p.ts })) {
          return { ...current, ...emptySession<P, R, A>() };
        }
        return { ...current, ...p, assistBusy: false };
      },
    },
  ));
}

/** Сесія сторінки «Імпорт виписки» (одна на браузер і акаунт). */
export const useBankImportSession = createBankImportSession({ persistKey: BANK_IMPORT_SESSION_KEY });
/** Режим «нерозпізнані» — без збереження (рядки — документи, їх дає сервер). */
export const useBankReviewSession = createBankImportSession();
